import { createSlice } from '@reduxjs/toolkit';
import { AUTOMATION_SOURCE, INVENTORY_PROPERTIES, PROPERTY_NOT_FOUND, PROCESS_TIME_FIELD, isUnknownSentinel } from 'utils/constants';
import { FENIX_EXPORTED_TO_YMS_FIELD, resolveLookupValueFromDisplay } from 'utils/fenixYms';
import { resolveProcessTimeFieldValue } from 'utils/processTime';
import { mergeMacawImages } from 'utils/macawImages';
import { readFromObj } from 'utils/utils';
import { applyValidationScope, buildBlockingEntry, getFieldCaption, resolveValidationScope } from 'utils/lookupBlocking';
const initialState = {
    fetchTransactionFormConfigInProgress: false,
    fetchTransactionFormConfigError: null,

    initializingFormInProgress: false,
    initializingFormError: null,

    submitFormInProgress: {},
    discardTransactionInProgress: {},
    startNewInProgress: {},
    discardReasonDialogOpenByTransactionId: {},
    discardReasonStartNewByTransactionId: {},
    discardReasonOpenedViaPulseShortcutByTransactionId: {},
    draftMailTransactionInProgress: {},
    draftMailDialogOpenByTransactionId: {},
    returnToQueueInProgress: false,
    returnToQueueError: false,

    inventoryMapping: {},
    inventoryData: {},
    equipmentData: {},
    recentValueData: [],

    // Source of the `latestCopy.id`s stamped on forms; never reset, so an id is never reused.
    copySequence: 0,

    forms: {},
};

const PARKED_SUBMIT_FIELDS = [
    'transactionId',
    'locationId',
    'tabId',
    'gateId',
    'dynamicForm',
    'dynamicFormData',
    'autoPopulateEmptyFormFields',
    'deferClose',
    'pulseShortcutUsed',
    // A replay re-blocked by a blocker that appeared after the first round (a slow validate widening the
    // lookup set) parks a submit that already carries the earlier overrides. Without the entry they are
    // stripped, and the BE stores no record of them, with no error anywhere.
    'blockSubmissionOverrides',
];

const parkSubmitPayload = (submitPayload) => {
    if (!submitPayload) return null;
    const parkedPayload = {};
    for (const field of PARKED_SUBMIT_FIELDS) {
        if (submitPayload[field] !== undefined) parkedPayload[field] = submitPayload[field];
    }
    return parkedPayload;
};

const formControlSlice = createSlice({
    name: 'formControl',
    initialState,
    reducers: {
        initializeNewTransactionFormStart(state) {
            state.initializingFormInProgress = true;
            state.initializingFormError = null;
        },
        initializeNewTransactionFormSuccess(state, { payload: { transactionData } }) {
            state.initializingFormInProgress = false;
            state.forms[transactionData.id] = {
                transactionData,
                transactionIdAnnouncementUsed: 0,
                relayUsed: !!transactionData?.reopened,
                internalIdAnnouncementUsed: '',
                activeSelectionTableName: '',
                externalAnnouncements: {},
                form: {
                    values: {},
                    errors: {},
                    touched: {},
                    userEdited: {},
                    fieldValueSources: {},
                    aiSuggestionHistory: {},
                    sheetAnnouncementValues: {},
                },
            };
        },
        initializeNewTransactionFormFailed(state, { paylodad: err }) {
            state.initializingFormInProgress = false;
            state.initializingFormError = err;
        },

        initializeNewForm(state, { payload: formId }) {
            state.forms[formId] = {
                form: {
                    values: {},
                    errors: {},
                    touched: {},
                    userEdited: {},
                    fieldValueSources: {},
                    aiSuggestionHistory: {},
                },
            };
        },
        removeForm(state, { payload: formId }) {
            // eslint-disable-next-line no-unused-vars
            const { [formId]: toBeDeleted, ...rest } = state.forms;
            state.forms = rest;

            // The transaction may be removed/replaced without ever going through
            // setDiscardReasonDialogOpen(open: false) (e.g. a sticky-lane replacement, or the panel
            // being closed from elsewhere) - clean up these per-transactionId flags here too, or they
            // go stale and leak onto whatever new form later reuses this same transactionId.
            // eslint-disable-next-line no-unused-vars
            const { [formId]: dialogOpenToRemove, ...restDialogOpen } = state.discardReasonDialogOpenByTransactionId;
            state.discardReasonDialogOpenByTransactionId = restDialogOpen;
            // eslint-disable-next-line no-unused-vars
            const { [formId]: startNewToRemove, ...restStartNew } = state.discardReasonStartNewByTransactionId;
            state.discardReasonStartNewByTransactionId = restStartNew;
            // eslint-disable-next-line no-unused-vars
            const { [formId]: openedViaShortcutToRemove, ...restOpenedViaShortcut } = state.discardReasonOpenedViaPulseShortcutByTransactionId;
            state.discardReasonOpenedViaPulseShortcutByTransactionId = restOpenedViaShortcut;
            if (state.inventoryMapping) delete state.inventoryMapping[formId];
        },

        registerNewFormField(state, { payload: { formId, fieldName, initialValue, disabled, required } }) {
            if (disabled) return;

            const form = state.forms[formId].form;

            // Do not clobber a value that was already restored/pre-seeded (e.g. when
            // rehydrating a reopened previously-discarded transaction). On a normal
            // first mount the field is absent from form.values, so behaviour is
            // unchanged; when a restored value exists we keep it.
            const alreadyPopulated =
                Object.prototype.hasOwnProperty.call(form.values, fieldName) &&
                form.values[fieldName] !== undefined &&
                form.values[fieldName] !== null &&
                form.values[fieldName] !== '';

            // Never store a sentinel as a real value — treat it as empty
            if (!alreadyPopulated) form.values[fieldName] = initialValue;
            if (!required) return;
            // If the field is already populated (restored value), it is valid.
            form.errors[fieldName] = !alreadyPopulated;
            form.touched[fieldName] = false;
        },

        setFieldValue(state, { payload: { formId, fieldName, value } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            // Never store a sentinel as a real value — treat it as empty
            form.values[fieldName] = value;
        },

        // Perf: combined action to avoid double dispatch on every keystroke
        setFieldValueAndMarkEdited(state, { payload: { formId, fieldName, value } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            // Never store a sentinel as a real value — treat it as empty
            form.values[fieldName] = value;
            if (!form.userEdited) form.userEdited = {};
            form.userEdited[fieldName] = true;
            if (!form.fieldValueSources) form.fieldValueSources = {};
            form.fieldValueSources[fieldName] = 'user';
        },

        setFieldTouched(state, { payload: { formId, fieldName } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            form.touched[fieldName] = true;
        },

        setFieldError(state, { payload: { formId, fieldName, error = true, helperText = '' } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            form.errors[fieldName] = error;
            if (!form.fieldHelperTexts) form.fieldHelperTexts = {};
            form.fieldHelperTexts[fieldName] = helperText;
        },

        clearFieldHelperText(state, { payload: { formId, fieldName } }) {
            const form = state.forms[formId]?.form;
            if (!form || !form.fieldHelperTexts) return;
            delete form.fieldHelperTexts[fieldName];
        },

        setSubmitFormInProgress(state, { payload: { formId, inProgress = true } }) {
            if (inProgress) state.submitFormInProgress[formId] = true;
            else {
                // eslint-disable-next-line
                const { [formId]: _, ...newSubmitFormInProgress } = state.submitFormInProgress;
                state.submitFormInProgress = newSubmitFormInProgress;
            }
        },

        setdiscardTransactionInProgress(state, { payload: { transactionId, inProgress = true } }) {
            if (inProgress) state.discardTransactionInProgress[transactionId] = true;
            else {
                // eslint-disable-next-line
                const { [transactionId]: _, ...newDiscardTransactionInProgress } = state.discardTransactionInProgress;
                state.discardTransactionInProgress = newDiscardTransactionInProgress;
            }
        },

        setDiscardReasonDialogOpen(state, { payload: { transactionId, open = true, startNew = false, openedViaPulseShortcut = false } }) {
            if (!transactionId) return;
            if (open) {
                state.discardReasonDialogOpenByTransactionId[transactionId] = true;
                // Remember whether this discard should also start a new transaction (sticky lane).
                if (startNew) state.discardReasonStartNewByTransactionId[transactionId] = true;
                // Remember whether this discard dialog was opened via the Ctrl+Shift+Delete "Quick
                // Discard + Pulse" shortcut - an interaction-source fact, NOT whether pulse is actually
                // eligible here (see selectAutomaticPulseDiscardEligibleByTransactionId). The real
                // discardTransactionStart dispatch happens later, once the agent picks a reason, so this
                // has to survive that round trip through state; eligibility is re-checked at that point.
                state.discardReasonOpenedViaPulseShortcutByTransactionId[transactionId] = openedViaPulseShortcut;
                return;
            }
            // eslint-disable-next-line
            const { [transactionId]: toBeRemoved, ...rest } = state.discardReasonDialogOpenByTransactionId;
            state.discardReasonDialogOpenByTransactionId = rest;
            // eslint-disable-next-line
            const { [transactionId]: startNewToRemove, ...restStartNew } = state.discardReasonStartNewByTransactionId;
            state.discardReasonStartNewByTransactionId = restStartNew;
            // eslint-disable-next-line
            const { [transactionId]: openedViaShortcutToRemove, ...restOpenedViaShortcut } = state.discardReasonOpenedViaPulseShortcutByTransactionId;
            state.discardReasonOpenedViaPulseShortcutByTransactionId = restOpenedViaShortcut;
        },

        // Sticky Lane "start new" - stays true for the whole orchestration (submit/discard + replacement
        // creation + activation), unlike setSubmitFormInProgress/setdiscardTransactionInProgress which clear
        // as soon as the PUT resolves. Drives the ProcessingOverlay so it doesn't disappear before the old
        // transaction is actually removed and the replacement activated.
        setStartNewInProgress(state, { payload: { transactionId, inProgress = true } }) {
            if (inProgress) {
                state.startNewInProgress[transactionId] = true;
                return;
            }
            // eslint-disable-next-line
            const { [transactionId]: toBeRemoved, ...rest } = state.startNewInProgress;
            state.startNewInProgress = rest;
        },

        setDraftMailTransactionInProgress(state, { payload: { transactionId, inProgress = true } }) {
            if (inProgress) {
                state.draftMailTransactionInProgress[transactionId] = true;
            } else {
                // eslint-disable-next-line
                const { [transactionId]: _, ...newDraftMailTransactionInProgress } = state.draftMailTransactionInProgress;
                state.draftMailTransactionInProgress = newDraftMailTransactionInProgress;
            }
        },

        setDraftMailDialogOpen(state, { payload: { transactionId, open = true } }) {
            if (!transactionId) return;
            if (open) {
                state.draftMailDialogOpenByTransactionId[transactionId] = true;
                return;
            }
            // eslint-disable-next-line
            const { [transactionId]: toBeRemoved, ...rest } = state.draftMailDialogOpenByTransactionId;
            state.draftMailDialogOpenByTransactionId = rest;
        },

        setReturnToQueueIsInProgress(state, { payload }) {
            state.returnToQueueInProgress = payload;
        },

        setReturnToQueueError(state, { payload }) {
            state.returnToQueueError = payload;
        },

        setTransactionData(state, { payload: { transactionData, setNewAutomation = false } }) {
            const formId = transactionData.id;
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            formObj.transactionData = transactionData;
            setNewAutomation && (formObj.form = { ...formObj.form, automation: readAutomation(formObj) });
        },
        setRelaysConfig(state, { payload: { transactionId, relaysConfig } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.relaysConfig = relaysConfig;
        },

        // automaticPulse: 'NONE' | 'SUBMIT' | 'ALL' - whether this lane auto-pulses its Pulse
        // relay(s) on submit, discard, both, or neither. See selectAutomaticPulse*EligibleByTransactionId.
        setAutomaticPulse(state, { payload: { transactionId, automaticPulse = 'NONE' } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.automaticPulse = automaticPulse;
        },

        setRelayUsed(state, { payload: { transactionId, relayUsed = true } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.relayUsed = relayUsed;
        },

        applyLookupBlockingValidation(state, { payload: { transactionId, fieldName, validationResult } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;

            const { validatedFields, autofilledFields } = resolveValidationScope(fieldName, validationResult);

            formObj.blockingValidations = applyValidationScope(formObj.blockingValidations, {
                validatedFields,
                autofilledFields,
                createEntry: (validatedField) =>
                    buildBlockingEntry({
                        fieldName: validatedField,
                        validationResult,
                        label: getFieldCaption(formObj.formConfig, validatedField),
                    }),
            });
        },

        clearLookupBlockingValidations(state, { payload: { transactionId, fields } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj?.blockingValidations) return;

            const newMap = { ...formObj.blockingValidations };
            (fields || []).forEach((fieldName) => {
                delete newMap[fieldName];
            });
            formObj.blockingValidations = newMap;
        },

        setRelayEnforcement(state, { payload: { transactionId, relayEnforcement } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.relayEnforcement = relayEnforcement;
        },

        // `ocrServerRejection`: the BE message when the BE, not the form, refused the OCR verification of this submit.
        openSubmitBlockDialog(
            state,
            { payload: { transactionId, startNew = false, blockers = [], payload: submitPayload = null, ocrServerRejection = null } }
        ) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            if (formObj.submitBlockDialog) return;

            formObj.submitBlockDialog = {
                startNew,
                blockers,
                payload: parkSubmitPayload(submitPayload),
                ...(ocrServerRejection && { ocrServerRejection }),
            };
        },

        closeSubmitBlockDialog(state, { payload: { transactionId } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.submitBlockDialog = null;
        },

        updateTransactionDataAutomation(state, { payload: { automation, isSplitViewMode = false } }) {
            const formId = automation.id;
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            const oldAutomation = formObj.form?.automation;
            formObj.transactionData.version = automation.version;
            formObj.transactionData.automation = automation;
            const newAutomation = readAutomation(formObj);
            formObj.form = { ...formObj.form, automation: newAutomation };
            if (isSplitViewMode) {
                // Background update (late AI detection): never overwrite an agent edit, whatever the source.
                applyAutomationToFormValues(formObj.form, newAutomation, oldAutomation, { preserveUserEdits: true });
            }
        },

        // Macaw images from `GET .../images` and TransactionImageStatusMessage, merged by id (see mergeMacawImages)
        setEncodedImages(state, { payload: { encodedImages, transactionId, imageName, refreshUrls = false } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            formObj.form.encodedImages = formObj.form?.encodedImages || [];
            mergeMacawImages(formObj.form.encodedImages, encodedImages, { imageName, refreshUrls });
        },

        // Snapshot IDs are currently time-based. Preserve that legacy fallback, but callers
        // should provide timestamp explicitly so a future UUID/string ID does not affect ordering.
        saveCameraStreamSnapshot(state, { payload: { snapshot, transactionId, imageCategory, id, imageName, cameraName, timestamp = id } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj) return;
            if (!formObj.form.cameraSnapshots) formObj.form.cameraSnapshots = [];
            formObj.form.cameraSnapshots.unshift({ id, imageCategory, encodedImage: snapshot, imageName, cameraName, timestamp });
        },

        removeCameraSnapshot(state, { payload: { transactionId, imageId } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj || !formObj.form.cameraSnapshots) return;
            formObj.form.cameraSnapshots = formObj.form.cameraSnapshots.filter((snapshot) => snapshot.id !== imageId);
        },

        // a Macaw image deleted by the agent
        removeMacawImage(state, { payload: { transactionId, imageId } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj || !formObj.form.encodedImages) return;
            formObj.form.encodedImages = formObj.form.encodedImages.filter((image) => image.id !== imageId);
        },

        clearFaceMatchSnapshots(state, { payload: { transactionId, imageCategory } }) {
            const formObj = state.forms?.[transactionId];
            if (!formObj || !formObj.form?.cameraSnapshots) return;
            formObj.form.cameraSnapshots = formObj.form.cameraSnapshots.filter((snapshot) => snapshot.imageCategory !== imageCategory);
        },

        setAutomationValues(
            state,
            {
                payload: {
                    automationObjTemplate,
                    transactionId,
                    newAutomationValues,
                    automationUpdates,
                    customSource,
                    setAlisaAutomation,
                    isSplitViewMode = false,
                },
            }
        ) {
            const formObj = state.forms[transactionId];
            // The form may have closed while a copy / autofill that targets it was still running.
            if (!formObj) return;
            const oldAutomation = formObj.form?.automation;
            // The whole automation, or only the entries an update merged in.
            let automationToApply;
            if (setAlisaAutomation) {
                formObj.form.automation = {};
                formObj.form = { ...formObj.form, automation: readAutomation(formObj) };
                automationToApply = formObj.form.automation;
            } else if (automationUpdates) {
                const { automation, mergedUpdates } = mergeAutomationUpdates(oldAutomation, automationUpdates);
                formObj.form.automation = automation;
                automationToApply = mergedUpdates;
            } else {
                formObj.form.automation = newAutomationValues ?? createAutomationValues(formObj, automationObjTemplate, customSource);
                automationToApply = formObj.form.automation;
            }

            if (isSplitViewMode) {
                // Every caller is an agent action (copy, un-copy, pick), so a value it writes is no longer the agent's.
                applyAutomationToFormValues(formObj.form, automationToApply, oldAutomation, { releaseUserEdits: true });
            }
        },

        setAnnouncementIdUsed(state, { payload: { formId, manualAnnouncementIdUsed, internalIdAnnouncementUsed } }) {
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            formObj.transactionIdAnnouncementUsed = manualAnnouncementIdUsed || 0;
            formObj.internalIdAnnouncementUsed = internalIdAnnouncementUsed || '';
        },

        resetTransactionFormState(state, { payload: transactionId }) {
            state.forms[transactionId] = {
                ...state.forms[transactionId],
                form: {
                    values: {},
                    errors: {},
                    touched: {},
                    userEdited: {},
                    fieldValueSources: {},
                },
            };
        },

        fetchTransactionFormConfigStart(state) {
            state.fetchTransactionFormConfigInProgress = true;
            state.fetchTransactionFormConfigError = null;
        },

        fetchTransactionFormConfigSuccess(state, { payload: { transactionId, transactionFormConfig, isSplitViewMode } }) {
            const formObj = state.forms[transactionId];
            state.fetchTransactionFormConfigInProgress = false;

            transactionFormConfig.fields.forEach((field) => {
                //TODO solve this on BE
                if (field.dataType === 'boolean') {
                    field.lookup = {
                        valueExpr: 'value',
                        displayExpr: 'label',
                        dataSource: [
                            { value: true, label: 'Yes' },
                            { value: false, label: 'No' },
                        ],
                    };
                }
            });

            if (formObj) {
                formObj.formConfig = {
                    ...transactionFormConfig,
                };

                formObj.form = {
                    ...formObj.form,
                    automation: readAutomation(formObj),
                };
                if (isSplitViewMode) {
                    applyAutomationToFormValues(formObj.form, formObj.form.automation, {});
                }
            }

            state.recentValueData = transactionFormConfig?.fields
                .filter(
                    (field) =>
                        INVENTORY_PROPERTIES.includes(field.dataField) &&
                        field.lookup &&
                        Array.isArray(field.lookup.dataSource) &&
                        field.lookup.dataSource.some((item) => item.value)
                )
                .map((field) => ({
                    dataField: field.dataField,
                    lookup: field.lookup,
                }));
        },
        fetchTransactionFormConfigFailed(state, { payload: error }) {
            state.fetchTransactionFormConfigInProgress = false;
            state.fetchTransactionFormConfigError = error;
        },

        setFormsObj(state, { payload: newFormsObject }) {
            state.forms = newFormsObject;

            for (const formId of Object.keys(state.inventoryMapping ?? {})) {
                if (!newFormsObject?.[formId]) delete state.inventoryMapping[formId];
            }
        },
        setActiveSelectionTableName(state, { payload: { formId, tableName = '' } }) {
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            formObj.activeSelectionTableName = tableName;
        },

        // The latest agent copy into a form (see lib/copyRequestGuard). Kept on the form, so a form that
        // closes or is re-created drops it and a copy still loading for it goes stale.
        beginCopy(state, { payload: { formId, source = null } }) {
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            state.copySequence = (state.copySequence ?? 0) + 1;
            formObj.latestCopy = { id: state.copySequence, source };
        },

        endCopy(state, { payload: { formId, copyId } }) {
            const formObj = state.forms?.[formId];
            if (formObj?.latestCopy?.id === copyId) formObj.latestCopy = null;
        },

        // A copy that applies at once (an announcement) makes any copy still loading stale.
        cancelCopies(state, { payload: { formId } }) {
            const formObj = state.forms?.[formId];
            if (formObj) formObj.latestCopy = null;
        },

        // A row copy dropped because a newer copy happened meanwhile; the row's table clears its highlight.
        // `seq` makes every drop a new value.
        setDroppedCopy(state, { payload: { formId, tableName, key } }) {
            const formObj = state.forms?.[formId];
            if (!formObj) return;
            formObj.droppedCopy = { tableName, key, seq: (formObj.droppedCopy?.seq ?? 0) + 1 };
        },

        setInventoryData(state, { payload: { inventoryData, locationId } }) {
            if (!inventoryData) {
                // eslint-disable-next-line
                const { [locationId]: _, ...newInventoryData } = state.inventoryData;
                state.inventoryData = newInventoryData;
                return;
            }
            state.inventoryData[locationId] = inventoryData;
        },
        fetchInventoryMappingStart(state, { payload: { formId, signature } }) {
            const previous = state.inventoryMapping[formId];
            // A result for this same payload stays visible, so tabbing through fields does not blink the
            // indicator. A result for other values is hidden until the new one arrives: it may be wrong.
            state.inventoryMapping[formId] =
                signature !== undefined && previous?.signature === signature
                    ? { ...previous, inProgress: true }
                    : { data: [], hasInventoryItemTypeConfiguration: false, inProgress: true, touched: false };
        },
        fetchInventoryMappingFailed(state, { payload: formId }) {
            state.inventoryMapping[formId] = {
                data: [],
                hasInventoryItemTypeConfiguration: false,
                inProgress: false,
                touched: false,
            };
        },
        setInventoryDataMapping(state, { payload: { inventoryMapping, formId, signature } }) {
            state.inventoryMapping[formId] = {
                data: inventoryMapping?.data || [],
                hasInventoryItemTypeConfiguration: inventoryMapping?.hasInventoryItemTypeConfiguration || false,
                inProgress: false,
                touched: true,
                signature,
            };
        },

        setExternalAnnouncements(state, { payload: { formId, announcementSource, announcements } }) {
            const formObj = state?.forms?.[formId];
            if (!formObj) return;
            formObj.externalAnnouncements[announcementSource] = announcements;
        },

        setEquipmentData(state, { payload: { equipmentData, locationId } }) {
            if (!equipmentData) {
                // eslint-disable-next-line
                const { [locationId]: _, ...newEquipmentData } = state.equipmentData;
                state.equipmentData = newEquipmentData;
                return;
            }
            state.equipmentData[locationId] = equipmentData;
        },

        markFieldUserEdited(state, { payload: { formId, fieldName } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            if (!form.userEdited) form.userEdited = {};
            form.userEdited[fieldName] = true;
            if (!form.fieldValueSources) form.fieldValueSources = {};
            form.fieldValueSources[fieldName] = 'user';
            form.errors[fieldName] = false;
        },

        setFieldValueSource(state, { payload: { formId, fieldName, source } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            if (!form.fieldValueSources) form.fieldValueSources = {};
            form.fieldValueSources[fieldName] = source;
        },
        setProcessTimeSuggestion(state, { payload: { formId, sourceValue, originalValue = null, writeFieldValue = false } }) {
            const formObj = state.forms[formId];
            const form = formObj?.form;
            if (!form) return;
            if (!form.automation) form.automation = {};

            if (writeFieldValue) {
                if (form.userEdited?.[PROCESS_TIME_FIELD]) return;
                if (!form.fieldValueSources) form.fieldValueSources = {};
                const lookup = formObj?.formConfig?.fields?.find((field) => field.dataField === PROCESS_TIME_FIELD)?.lookup;
                const resolvedValue = resolveProcessTimeFieldValue(sourceValue, lookup);
                const currentValue = form.values[PROCESS_TIME_FIELD];
                const currentSource = form.fieldValueSources[PROCESS_TIME_FIELD];
                const fieldEmpty = currentValue == null || currentValue === '';
                if (!fieldEmpty && currentSource !== AUTOMATION_SOURCE.AUTO) return;
                if (currentValue === resolvedValue && currentSource === AUTOMATION_SOURCE.AUTO) return;
                form.values[PROCESS_TIME_FIELD] = resolvedValue;
                form.fieldValueSources[PROCESS_TIME_FIELD] = AUTOMATION_SOURCE.AUTO;
                form.errors[PROCESS_TIME_FIELD] = false;
                form.automation[PROCESS_TIME_FIELD] = { source: AUTOMATION_SOURCE.AUTO, sourceValue, originalValue };
                return;
            }

            const current = form.automation[PROCESS_TIME_FIELD];
            if (current?.source === AUTOMATION_SOURCE.AUTO && current?.sourceValue === sourceValue) return;
            form.automation[PROCESS_TIME_FIELD] = {
                source: AUTOMATION_SOURCE.AUTO,
                sourceValue,
                originalValue,
            };
        },

        setExportedToYmsSuggestion(state, { payload: { formId, sourceValue, originalValue = null, writeFieldValue = false } }) {
            const EXPORTED_TO_YMS_FIELD = FENIX_EXPORTED_TO_YMS_FIELD;
            const formObj = state.forms[formId];
            const form = formObj?.form;
            if (!form) return;
            if (!form.automation) form.automation = {};
            if (!form.fieldValueSources) form.fieldValueSources = {};

            if (writeFieldValue) {
                if (form.userEdited?.[EXPORTED_TO_YMS_FIELD]) {
                    form.automation[EXPORTED_TO_YMS_FIELD] = { source: AUTOMATION_SOURCE.AUTO, sourceValue, originalValue };
                    return;
                }
                const lookup = formObj?.formConfig?.fields?.find((field) => field.dataField === EXPORTED_TO_YMS_FIELD)?.lookup;
                const resolvedValue = resolveLookupValueFromDisplay(sourceValue, lookup);
                const currentValue = form.values[EXPORTED_TO_YMS_FIELD];
                const currentSource = form.fieldValueSources[EXPORTED_TO_YMS_FIELD];
                const fieldEmpty = currentValue == null || currentValue === '';
                if (!fieldEmpty && currentSource !== AUTOMATION_SOURCE.AUTO) return;
                if (currentValue === resolvedValue && currentSource === AUTOMATION_SOURCE.AUTO) return;

                form.values[EXPORTED_TO_YMS_FIELD] = resolvedValue;
                form.fieldValueSources[EXPORTED_TO_YMS_FIELD] = AUTOMATION_SOURCE.AUTO;
                form.errors[EXPORTED_TO_YMS_FIELD] = false;
                form.automation[EXPORTED_TO_YMS_FIELD] = { source: AUTOMATION_SOURCE.AUTO, sourceValue, originalValue };
                return;
            }

            const current = form.automation[EXPORTED_TO_YMS_FIELD];

            if (current?.source === AUTOMATION_SOURCE.AUTO && current?.sourceValue === sourceValue) return;
            form.automation[EXPORTED_TO_YMS_FIELD] = {
                source: AUTOMATION_SOURCE.AUTO,
                sourceValue,
                originalValue,
            };
        },

        applyAutomationToFieldValues(state, { payload: { formId, automationEntries } }) {
            const formObj = state.forms[formId];
            if (!formObj?.form) return;
            const form = formObj.form;
            if (!form.fieldValueSources) form.fieldValueSources = {};
            if (!form.aiSuggestionHistory) form.aiSuggestionHistory = {};

            const now = Date.now();
            automationEntries.forEach(({ fieldName, value, source }) => {
                // '?'/'-1'/'UNKNOWN' are display-only "unknown" sentinels. Never
                // write them as a real form value or clear the required error,
                // otherwise the field looks filled and validation never fires.

                form.values[fieldName] = value;
                form.fieldValueSources[fieldName] = source;
                form.errors[fieldName] = false;

                if (source === AUTOMATION_SOURCE.AI_OCR && value != null && value !== '' && !isUnknownSentinel(value)) {
                    if (!form.aiSuggestionHistory[fieldName]) form.aiSuggestionHistory[fieldName] = [];
                    const history = form.aiSuggestionHistory[fieldName];
                    const isDuplicate = history.some((entry) => entry.value === value);
                    if (!isDuplicate) {
                        history.unshift({ value, timestamp: now });
                        if (history.length > 5) history.pop();
                    }
                }
            });
        },

        setSheetAnnouncementValues(state, { payload: { formId, sheetAnnouncementValues } }) {
            const form = state.forms[formId]?.form;
            if (!form) return;
            form.sheetAnnouncementValues = sheetAnnouncementValues;
        },

        clearSheetAnnouncementValue(state, { payload: { formId, fieldName } }) {
            const form = state.forms[formId]?.form;
            if (!form || !form.sheetAnnouncementValues) return;
            delete form.sheetAnnouncementValues[fieldName];
        },

        clearUserEdited(state, { payload: { formId, fieldName } }) {
            const form = state.forms[formId]?.form;
            if (!form || !form.userEdited) return;
            delete form.userEdited[fieldName];
        },

        validateTransactionFormField() {},
        validateTransactionWithInventoryData() {},
        activateRelay() {},
        copyAppointmentData() {},
        copyBisonAnnouncement() {},
        copyConditionalsAutofill() {},
        copyLocationTransactionPartial() {},
        copyAppointmentDataPartial() {},
        copySheetAnnouncement() {},
        copyLocationTransaction() {},
        discardTransactionStart() {},
        discardTransactionAndStartNewStart() {},
        draftMailStart() {},
        submitFormStart() {},
        submitFormAndStartNewStart() {},
        overrideSubmitBlockStart() {},
        returnToQueueTransactionsStart() {},
        discardMultipleTransactionsStart() {},
        initializeEditTransactionForm() {},
        editFormStart() {},
        removeTransaction() {},
    },
});

export const formControlActions = formControlSlice.actions;
export const formControlReducer = formControlSlice.reducer;

// pure helper functions
function readAutomation(formObj) {
    const {
        transactionData: { automation, form: dynamicForm } = {},
        formConfig: { fields } = {},
        form: { automation: currentAutomation } = {},
    } = formObj;

    if (!automation || !fields || typeof automation !== 'object' || typeof fields !== 'object') return {};

    if (dynamicForm) {
        const flat = flattenAutomation(automation);
        if (currentAutomation?.[PROCESS_TIME_FIELD]?.source === AUTOMATION_SOURCE.AUTO) {
            flat[PROCESS_TIME_FIELD] = currentAutomation[PROCESS_TIME_FIELD];
        }
        return flat;
    }

    return fields.reduce((newAutomationValues, { dataField: fieldName, lookup, referenceField }) => {
        if (fieldName === PROCESS_TIME_FIELD) return newAutomationValues;
        let fieldAutomation = readFromObj(automation, fieldName);
        if (fieldAutomation === PROPERTY_NOT_FOUND) return newAutomationValues;

        if (currentAutomation?.[fieldName]?.source && currentAutomation?.[fieldName]?.source !== AUTOMATION_SOURCE.AI_OCR) return newAutomationValues;

        const sourceValue =
            (lookup?.dataSource
                ? lookup.displayExpr
                    ? lookup.dataSource.find((option) => option[lookup.valueExpr] === fieldAutomation?.value)?.[lookup.displayExpr]
                    : fieldAutomation.value
                : fieldAutomation.value) ?? (isFieldFreeSolo(lookup, referenceField) ? fieldAutomation.value : null);
        if (fieldAutomation?.source && sourceValue !== '-1')
            newAutomationValues[fieldName] = {
                source: fieldAutomation?.source,
                sourceValue,
                originalValue: fieldAutomation.value,
                ...(fieldAutomation?.source === AUTOMATION_SOURCE.AI_OCR && { image: true }),
            };

        return newAutomationValues;
    }, currentAutomation || {});
}

// clause taken from insputSelect.jsx and Select.jsx components
function isFieldFreeSolo(lookup, referenceField) {
    return !lookup?.displayExpr || referenceField;
}

function flattenAutomation(input, prefix = '', result = {}) {
    for (const [key, value] of Object.entries(input)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (value && typeof value === 'object' && Object.hasOwn(value, 'source') && Object.hasOwn(value, 'value')) {
            result[fullKey] = {
                source: value.source,
                sourceValue: value.value === '-1' ? '?' : value.value,
                originalValue: value.value,
            };
        } else if (value && typeof value === 'object') {
            flattenAutomation(value, fullKey, result);
        }
    }

    return result;
}

function createAutomationValues(formObj, automationObjTemplate, customSource = {}) {
    const {
        formConfig: { fields },
        form: { automation },
        transactionData,
    } = formObj;

    const { values, fieldMappings } = customSource;

    const initialAcc =
        automation?.[PROCESS_TIME_FIELD]?.source === AUTOMATION_SOURCE.AUTO ? { [PROCESS_TIME_FIELD]: automation[PROCESS_TIME_FIELD] } : {};

    return fields.reduce((newAutomations, { dataField: fieldName, lookup, editable }) => {
        if (!editable || fieldName === 'sendToBison') return newAutomations; //TODO do not harcode ask for flags from BE
        if (fieldName === PROCESS_TIME_FIELD) return newAutomations;

        const fieldCurrentAutomation = !!automation && automation[fieldName];

        const source = values || transactionData;
        const dataFieldToRead = fieldMappings ? fieldMappings[fieldName] : fieldName;

        if (!dataFieldToRead) return newAutomations;

        const sourceValue = getSourceValue(source, dataFieldToRead, lookup);
        const originalValue = getOriginalValue(source, dataFieldToRead, lookup);

        if (!sourceValue || typeof sourceValue === 'object') {
            if (fieldCurrentAutomation && fieldCurrentAutomation.source === AUTOMATION_SOURCE.AI_OCR)
                newAutomations[fieldName] = fieldCurrentAutomation;
            return newAutomations;
        }

        newAutomations[fieldName] = { sourceValue, ...(originalValue && { originalValue }), ...automationObjTemplate };

        return newAutomations;
    }, initialAcc);
}

/**
 * Merges a caller's own entries (driver / inventory / equipment autofill, cell copies) into the CURRENT
 * automation, so several actions from one pick never put each other's older entries back. Only
 * `mergedUpdates` is applied to the form.
 *
 * Equipment is LOWER PRIORITY: its entry never replaces a suggestion the field already has.
 */
function mergeAutomationUpdates(currentAutomation, automationUpdates) {
    const automation = { ...(currentAutomation || {}) };
    const mergedUpdates = {};
    Object.entries(automationUpdates).forEach(([fieldName, entry]) => {
        if (entry?.source === AUTOMATION_SOURCE.EQUIPMENT_ITEM && automation[fieldName]?.sourceValue) return;
        automation[fieldName] = entry;
        mergedUpdates[fieldName] = entry;
    });
    return { automation, mergedUpdates };
}

const isSameLookupPick = (a, b) => a.originalValue === b.originalValue && a.sourceValue === b.sourceValue;

/**
 * `preserveUserEdits` (background version updates) keeps every agent edit, whatever the source.
 * `releaseUserEdits` (agent actions) drops the edit flag of a field it overwrites.
 */
function applyAutomationToFormValues(form, newAutomation, oldAutomation, { preserveUserEdits = false, releaseUserEdits = false } = {}) {
    if (!newAutomation) return;
    if (!form.fieldValueSources) form.fieldValueSources = {};
    if (!form.aiSuggestionHistory) form.aiSuggestionHistory = {};
    // Last driver / inventory pick that suggested each field, kept across picks that suggest nothing for it.
    if (!form.lookupAutofillPicks) form.lookupAutofillPicks = {};

    const now = Date.now();
    Object.entries(newAutomation).forEach(([fieldName, automationData]) => {
        if (!automationData?.sourceValue && !automationData?.originalValue) return;

        const { source, originalValue, sourceValue } = automationData || {};

        if (source === AUTOMATION_SOURCE.AUTO) return;

        const isAlisa = source === AUTOMATION_SOURCE.AI_OCR;
        const isEquipment = source === AUTOMATION_SOURCE.EQUIPMENT_ITEM;
        const isLookupAutofill = source === AUTOMATION_SOURCE.INVENTORY_ITEM || source === AUTOMATION_SOURCE.AUTH_LIST;
        const isFieldUserEdited = form.userEdited?.[fieldName];
        // '/' is the "unknown" placeholder, not a real value - autofill may still fill a field holding it.
        const fieldHasValue = form.values[fieldName] != null && form.values[fieldName] !== '' && form.values[fieldName] !== '/';

        // If user edited and source is alisa, don't override. Background updates
        // (preserveUserEdits) also keep user edits over any other source.
        if (isFieldUserEdited && (isAlisa || preserveUserEdits)) return;

        // Equipment is LOWER PRIORITY - don't override existing values
        if (isEquipment && fieldHasValue) return;

        const oldAuto = oldAutomation?.[fieldName];

        // Driver / inventory autofill of sibling fields (a chassis pick fills the trailer #, a driver pick the
        // phone #) keeps a value the agent typed - unless this pick differs from the last one of the same
        // source that suggested the field: the correction belonged to that earlier pick.
        if (isLookupAutofill) {
            const previousPick = form.lookupAutofillPicks[fieldName];
            form.lookupAutofillPicks[fieldName] = { source, originalValue, sourceValue };
            if (isFieldUserEdited && fieldHasValue) {
                const isDifferentPick = previousPick?.source === source && !isSameLookupPick(previousPick, automationData);
                if (!isDifferentPick) return;
            }
        }

        // If originalValue is a number (e.g., company.id), use sourceValue for form display
        // Numbers are used as auxiliary data (IDs) for matching, not as form values
        const isOriginalValueAnId = typeof originalValue === 'number';
        const valueToSet = isOriginalValueAnId ? sourceValue : (originalValue ?? sourceValue);

        // '?'/'-1'/'UNKNOWN' are display-only "unknown" sentinels (BE sends '-1'
        // or 'UNKNOWN', mapped to '?' for the Automation badge). They must not be
        // written as a real form value, and must NOT clear the required error —
        // otherwise the field looks filled and validation never fires on submit.
        // Check sentinel before the dedup guard so we never accidentally write
        // a sentinel on first-time application either.
        if (isUnknownSentinel(valueToSet)) {
            form.fieldValueSources[fieldName] = source;
            return;
        }

        // Only apply if automation value actually changed or first time
        if (oldAuto?.originalValue === originalValue && oldAuto?.source === source && form.values[fieldName] === valueToSet) return;

        form.values[fieldName] = valueToSet;
        form.fieldValueSources[fieldName] = source;
        form.errors[fieldName] = false;
        // Another source now owns the value, so a later edit corrects that value, not the earlier pick.
        if (!isLookupAutofill) delete form.lookupAutofillPicks[fieldName];
        // As the sheet / Bison announcement copies do: the rules that keep agent edits stop applying here.
        if (releaseUserEdits && form.userEdited) delete form.userEdited[fieldName];

        if (isAlisa && valueToSet != null && valueToSet !== '' && !isUnknownSentinel(valueToSet)) {
            if (!form.aiSuggestionHistory[fieldName]) form.aiSuggestionHistory[fieldName] = [];
            const history = form.aiSuggestionHistory[fieldName];
            const isDuplicate = history.some((entry) => entry.value === valueToSet);
            if (!isDuplicate) {
                history.unshift({ value: valueToSet, timestamp: now });
                if (history.length > 5) history.pop();
            }
        }
    });
}

export function getSourceValue(source, fieldName, lookup) {
    let fieldValue = readFromObj(source, fieldName);

    if (fieldValue == undefined || fieldValue === PROPERTY_NOT_FOUND) return null;

    return lookup?.dataSource
        ? lookup.dataSource.find((option) => {
              const findExpr = lookup.valueExpr || lookup.displayExpr;
              const valueToCompare = typeof fieldValue === 'object' ? fieldValue[findExpr] : fieldValue;
              return option[findExpr] === valueToCompare;
          })?.[lookup.displayExpr] ||
              fieldValue?.[lookup.displayExpr] ||
              fieldValue
        : fieldValue;
}

export function getOriginalValue(source, fieldName, lookup) {
    let fieldValue = readFromObj(source, fieldName);

    if (fieldValue == undefined || fieldValue === PROPERTY_NOT_FOUND) return null;

    return lookup?.dataSource
        ? lookup.dataSource.find((option) => {
              const findExpr = lookup.valueExpr || lookup.displayExpr;
              const valueToCompare = typeof fieldValue === 'object' ? fieldValue[findExpr] : fieldValue;
              return option[findExpr] === valueToCompare;
          })?.[lookup.valueExpr] ||
              fieldValue?.[lookup.valueExpr] ||
              fieldValue
        : fieldValue;
}
